import React, { useEffect, useMemo, useState } from 'react';
import { DollarSign, Receipt, Users, Landmark, TrendingUp, Download } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
const input = { width: '100%', padding: '0.65rem 0.8rem', borderRadius: '0.65rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
const btn = { padding: '0.55rem 0.85rem', borderRadius: '0.65rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600 };
const btnMuted = { padding: '0.5rem 0.75rem', borderRadius: '0.6rem', background: 'rgba(15,23,42,0.45)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.15)' };
const hint = { color: '#94a3b8', fontSize: '0.78rem', lineHeight: 1.4 };

const dollars = (cents) => `$${(Number(cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const currencyOptions = [
  { code: 'USD', label: 'USD - United States' },
  { code: 'EUR', label: 'EUR - Eurozone' },
  { code: 'GBP', label: 'GBP - United Kingdom' },
  { code: 'GHS', label: 'GHS - Ghana' },
  { code: 'NGN', label: 'NGN - Nigeria' },
  { code: 'KES', label: 'KES - Kenya' },
  { code: 'ZAR', label: 'ZAR - South Africa' },
  { code: 'UGX', label: 'UGX - Uganda' },
  { code: 'TZS', label: 'TZS - Tanzania' },
  { code: 'RWF', label: 'RWF - Rwanda' },
];

function MiniCard({ icon: Icon, label, value, tone = '#22d3ee' }) {
  return (
    <div style={{ ...card, padding: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
        <Icon style={{ width: '1rem', height: '1rem', color: tone }} />
        <span style={{ fontSize: '0.82rem' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

export default function Financials() {
  const {
    financialSummary, fetchFinancialSummary,
    expenses, fetchExpenses, addExpense, removeExpense,
    teamPayouts, fetchTeamPayouts, addTeamPayout, removeTeamPayout, changeTeamPayoutStatus,
    taxRemittances, fetchTaxRemittances, addTaxRemittance, removeTaxRemittance,
    contractors, fetchContractors, addContractor, removeContractor,
    timeEntries, fetchTimeEntries, addTimeEntry, removeTimeEntry,
    financialReminders, fetchFinancialReminders, runMonthlyReminderCycle, resolveReminder,
    generateAutoDraftPayouts, runStripeFeeSync,
    projects, fetchProjects,
    exportFinancialCsv,
  } = useApp();

  const [range, setRange] = useState(() => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const to = now.toISOString().slice(0, 10);
    return { from, to };
  });
  const [msg, setMsg] = useState('');
  const [fxLoading, setFxLoading] = useState(false);
  const [payoutStatusFilter, setPayoutStatusFilter] = useState('all');
  const [expenseForm, setExpenseForm] = useState({ category: 'software', vendor: '', amount: '', paidAt: '', note: '' });
  const [contractorForm, setContractorForm] = useState({ name: '', email: '', payoutMethod: 'worldremit', defaultCurrency: 'USD', note: '' });
  const [timeEntryForm, setTimeEntryForm] = useState({ project: '', contractor: '', entryDate: '', hours: '', hourlyRate: '', currency: 'USD', fxRateToUsd: 1, note: '' });
  const [autoDraftRange, setAutoDraftRange] = useState({ from: '', to: '' });
  const [payoutForm, setPayoutForm] = useState({
    contractor: '',
    contractorName: '',
    contractorEmail: '',
    payoutMethod: 'worldremit',
    amount: '',
    paidAt: '',
    project: '',
    status: 'draft',
    sourceCurrency: 'USD',
    sourceAmount: '',
    fxRateToUsd: '',
    transferFee: '',
    note: '',
  });
  const [taxForm, setTaxForm] = useState({ jurisdiction: 'US', periodLabel: '', amount: '', paidAt: '', reference: '', note: '' });

  useEffect(() => {
    fetchFinancialSummary(range);
    fetchExpenses({ limit: 50 });
    fetchTeamPayouts({ limit: 50, ...(payoutStatusFilter !== 'all' ? { status: payoutStatusFilter } : {}) });
    fetchTaxRemittances({ limit: 50 });
    fetchContractors({ activeOnly: true });
    fetchTimeEntries({ limit: 50 });
    fetchFinancialReminders({ status: 'open' });
    fetchProjects();
  }, [fetchFinancialSummary, fetchExpenses, fetchTeamPayouts, fetchTaxRemittances, fetchContractors, fetchTimeEntries, fetchFinancialReminders, fetchProjects, range.from, range.to, payoutStatusFilter]);

  const k = financialSummary?.kpis || {};
  const breakdowns = financialSummary?.breakdowns || {};

  const expenseRows = useMemo(() => (expenses || []).slice(0, 10), [expenses]);
  const payoutRows = useMemo(() => (teamPayouts || []).slice(0, 12), [teamPayouts]);
  const taxRows = useMemo(() => (taxRemittances || []).slice(0, 10), [taxRemittances]);
  const contractorRows = useMemo(() => (contractors || []).slice(0, 10), [contractors]);
  const timeRows = useMemo(() => (timeEntries || []).slice(0, 8), [timeEntries]);
  const reminderRows = useMemo(() => (financialReminders || []).slice(0, 6), [financialReminders]);

  const refresh = async () => {
    await Promise.all([
      fetchFinancialSummary(range),
      fetchExpenses({ limit: 50 }),
      fetchTeamPayouts({ limit: 50, ...(payoutStatusFilter !== 'all' ? { status: payoutStatusFilter } : {}) }),
      fetchTaxRemittances({ limit: 50 }),
      fetchContractors({ activeOnly: true }),
      fetchTimeEntries({ limit: 50 }),
      fetchFinancialReminders({ status: 'open' }),
    ]);
  };

  const flash = (text, timeout = 3000) => {
    setMsg(text);
    setTimeout(() => setMsg(''), timeout);
  };

  const onAddExpense = async (e) => {
    e.preventDefault();
    try {
      await addExpense({ ...expenseForm, amount: Number(expenseForm.amount) });
      setExpenseForm({ category: 'software', vendor: '', amount: '', paidAt: '', note: '' });
      await refresh();
      flash('Expense recorded');
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to save expense', 3500);
    }
  };

  const onAddContractor = async (e) => {
    e.preventDefault();
    try {
      await addContractor(contractorForm);
      setContractorForm({ name: '', email: '', payoutMethod: 'worldremit', defaultCurrency: 'USD', note: '' });
      await refresh();
      flash('Contractor added');
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to add contractor', 3500);
    }
  };

  const onSelectContractor = (contractorId) => {
    const contractor = (contractors || []).find((x) => x._id === contractorId);
    setPayoutForm((prev) => ({
      ...prev,
      contractor: contractorId,
      contractorName: contractor ? contractor.name : prev.contractorName,
      contractorEmail: contractor ? contractor.email : prev.contractorEmail,
      payoutMethod: contractor ? contractor.payoutMethod : prev.payoutMethod,
      sourceCurrency: contractor ? contractor.defaultCurrency : prev.sourceCurrency,
    }));
  };

  const onAddPayout = async (e) => {
    e.preventDefault();
    try {
      await addTeamPayout({
        ...payoutForm,
        amount: Number(payoutForm.amount),
        sourceAmount: payoutForm.sourceAmount ? Number(payoutForm.sourceAmount) : undefined,
        fxRateToUsd: payoutForm.fxRateToUsd ? Number(payoutForm.fxRateToUsd) : undefined,
        transferFee: payoutForm.transferFee ? Number(payoutForm.transferFee) : undefined,
      });
      setPayoutForm({
        contractor: '',
        contractorName: '',
        contractorEmail: '',
        payoutMethod: 'worldremit',
        amount: '',
        paidAt: '',
        project: '',
        status: 'draft',
        sourceCurrency: 'USD',
        sourceAmount: '',
        fxRateToUsd: '',
        transferFee: '',
        note: '',
      });
      await refresh();
      flash('Contractor payout recorded');
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to save payout', 3500);
    }
  };

  useEffect(() => {
    const sourceAmount = Number(payoutForm.sourceAmount);
    const fxRate = Number(payoutForm.fxRateToUsd);
    const transferFee = Number(payoutForm.transferFee || 0);
    if (Number.isFinite(sourceAmount) && sourceAmount > 0 && Number.isFinite(fxRate) && fxRate > 0) {
      const computed = Math.max(0, (sourceAmount * fxRate) - (Number.isFinite(transferFee) ? transferFee : 0));
      if (computed > 0) {
        setPayoutForm((prev) => ({ ...prev, amount: computed.toFixed(2) }));
      }
    }
  }, [payoutForm.sourceAmount, payoutForm.fxRateToUsd, payoutForm.transferFee]);

  const onAddTimeEntry = async (e) => {
    e.preventDefault();
    try {
      await addTimeEntry({
        ...timeEntryForm,
        hours: Number(timeEntryForm.hours),
        hourlyRate: Number(timeEntryForm.hourlyRate),
        fxRateToUsd: Number(timeEntryForm.fxRateToUsd || 1),
      });
      setTimeEntryForm({ project: '', contractor: '', entryDate: '', hours: '', hourlyRate: '', currency: 'USD', fxRateToUsd: 1, note: '' });
      await refresh();
      flash('Time entry added');
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to add time entry', 3500);
    }
  };

  const onAutoDraft = async (finalize = false) => {
    try {
      const result = await generateAutoDraftPayouts({ ...autoDraftRange, finalize });
      await refresh();
      if (finalize) flash(`Auto-drafted ${result?.created?.length || 0} payout(s)`);
      else flash(`Preview groups: ${result?.preview?.length || 0}`);
    } catch (err) {
      flash(err.response?.data?.error || 'Auto-draft failed', 3500);
    }
  };

  const onSyncStripeFees = async () => {
    try {
      const result = await runStripeFeeSync(range);
      await refresh();
      flash(`Stripe fee sync: ${result?.synced || 0} inserted`);
    } catch (err) {
      flash(err.response?.data?.error || 'Stripe fee sync failed', 3500);
    }
  };

  const onRunMonthly = async () => {
    try {
      await runMonthlyReminderCycle();
      await refresh();
      flash('Monthly tax reminder cycle ran');
    } catch (err) {
      flash(err.response?.data?.error || 'Monthly cycle failed', 3500);
    }
  };

  const onFetchLiveFx = async () => {
    try {
      const source = String(payoutForm.sourceCurrency || 'USD').toUpperCase();
      if (source === 'USD') {
        setPayoutForm((prev) => ({ ...prev, fxRateToUsd: '1.0000' }));
        flash('FX rate set to 1.0000 (USD to USD)');
        return;
      }
      setFxLoading(true);
      const resp = await fetch(`https://open.er-api.com/v6/latest/${source}`);
      const json = await resp.json();
      const usdRate = Number(json?.rates?.USD);
      if (!Number.isFinite(usdRate) || usdRate <= 0) {
        throw new Error('FX provider did not return a valid USD rate');
      }
      setPayoutForm((prev) => ({ ...prev, fxRateToUsd: usdRate.toFixed(6) }));
      flash(`Live FX loaded: 1 ${source} = ${usdRate.toFixed(6)} USD`);
    } catch (err) {
      flash(err?.message || 'Failed to fetch live FX', 3500);
    } finally {
      setFxLoading(false);
    }
  };

  const onApproveAllVisibleDrafts = async () => {
    try {
      const draftRows = (payoutRows || []).filter((p) => (p.status || 'paid') === 'draft');
      if (!draftRows.length) {
        flash('No visible draft payouts to approve');
        return;
      }
      await Promise.all(draftRows.map((p) => changeTeamPayoutStatus(p._id, 'approved')));
      await refresh();
      flash(`Approved ${draftRows.length} visible draft payout(s)`);
    } catch (err) {
      flash(err.response?.data?.error || 'Bulk approve failed', 3500);
    }
  };

  const onAddTax = async (e) => {
    e.preventDefault();
    try {
      await addTaxRemittance({ ...taxForm, amount: Number(taxForm.amount) });
      setTaxForm({ jurisdiction: 'US', periodLabel: '', amount: '', paidAt: '', reference: '', note: '' });
      await refresh();
      flash('Tax remittance recorded');
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to save tax remittance', 3500);
    }
  };

  const exportCsv = async (resource) => {
    try {
      await exportFinancialCsv(resource, range);
      flash(`Exported ${resource}.csv`);
    } catch (err) {
      flash(err.response?.data?.error || 'Export failed', 3500);
    }
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Financials</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={range.from} onChange={(e) => setRange((prev) => ({ ...prev, from: e.target.value }))} style={input} />
          <input type="date" value={range.to} onChange={(e) => setRange((prev) => ({ ...prev, to: e.target.value }))} style={input} />
          <button type="button" onClick={() => exportCsv('expenses')} style={btnMuted}><Download size={14} style={{ marginRight: 6 }} />Expenses</button>
          <button type="button" onClick={() => exportCsv('team-payouts')} style={btnMuted}><Download size={14} style={{ marginRight: 6 }} />Payouts</button>
          <button type="button" onClick={() => exportCsv('tax-remittances')} style={btnMuted}><Download size={14} style={{ marginRight: 6 }} />Tax</button>
          <button type="button" onClick={() => exportCsv('contractors')} style={btnMuted}><Download size={14} style={{ marginRight: 6 }} />Contractors</button>
        </div>
      </div>

      {msg ? <div style={{ marginBottom: '0.8rem', color: '#93c5fd', fontSize: '0.9rem' }}>{msg}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.9rem', marginBottom: '1rem' }}>
        <MiniCard icon={DollarSign} label="Cash In" value={dollars(k.netCashInCents)} tone="#34d399" />
        <MiniCard icon={Receipt} label="Processing Fees" value={dollars(k.processingFeesCents)} tone="#f59e0b" />
        <MiniCard icon={Users} label="Paid Payouts" value={dollars(k.contractorPayoutsCents)} tone="#22d3ee" />
        <MiniCard icon={Landmark} label="Tax Owed (Total)" value={dollars(k.taxOwedTotalCents)} tone="#f97316" />
        <MiniCard icon={TrendingUp} label="Owner Net (After Tax Reserve)" value={dollars(k.ownerNetAfterTaxReserveCents)} tone="#a78bfa" />
      </div>

      <div style={{ ...card, padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <strong style={{ marginRight: '0.5rem' }}>Automation</strong>
          <input type="date" value={autoDraftRange.from} onChange={(e) => setAutoDraftRange((p) => ({ ...p, from: e.target.value }))} style={input} />
          <input type="date" value={autoDraftRange.to} onChange={(e) => setAutoDraftRange((p) => ({ ...p, to: e.target.value }))} style={input} />
          <button type="button" onClick={() => onAutoDraft(false)} style={btnMuted}>Preview Auto-Draft</button>
          <button type="button" onClick={() => onAutoDraft(true)} style={btnMuted}>Create Drafts</button>
          <button type="button" onClick={onSyncStripeFees} style={btnMuted}>Sync Stripe Fees</button>
          <button type="button" onClick={onRunMonthly} style={btnMuted}>Run Monthly Tax Reminder</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '1rem' }}>
        <section style={{ ...card, padding: '1rem' }}>
          <h2 style={{ fontSize: '1rem', marginBottom: '0.6rem' }}>Log Expense</h2>
          <form onSubmit={onAddExpense} style={{ display: 'grid', gap: '0.5rem' }}>
            <select value={expenseForm.category} onChange={(e) => setExpenseForm((p) => ({ ...p, category: e.target.value }))} style={input}>
              {['software', 'marketing', 'operations', 'tax', 'other'].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <input value={expenseForm.vendor} onChange={(e) => setExpenseForm((p) => ({ ...p, vendor: e.target.value }))} placeholder="Vendor" style={input} />
            <input type="number" min="0.01" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))} placeholder="Amount" style={input} required />
            <input type="date" value={expenseForm.paidAt} onChange={(e) => setExpenseForm((p) => ({ ...p, paidAt: e.target.value }))} style={input} />
            <input value={expenseForm.note} onChange={(e) => setExpenseForm((p) => ({ ...p, note: e.target.value }))} placeholder="Note" style={input} />
            <button type="submit" style={btn}>Add Expense</button>
          </form>
          <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.35rem' }}>
            {expenseRows.map((e) => (
              <div key={e._id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.85rem', color: '#cbd5e1' }}>
                <span>{(e.category || 'other')} {e.vendor ? `- ${e.vendor}` : ''}</span>
                <span>{dollars(e.amountCents)} <button onClick={() => removeExpense(e._id).then(refresh)} style={{ color: '#f87171', marginLeft: '0.4rem' }}>x</button></span>
              </div>
            ))}
          </div>
        </section>

        <section style={{ ...card, padding: '1rem' }}>
          <h2 style={{ fontSize: '1rem', marginBottom: '0.6rem' }}>Contractors</h2>
          <form onSubmit={onAddContractor} style={{ display: 'grid', gap: '0.5rem' }}>
            <input value={contractorForm.name} onChange={(e) => setContractorForm((p) => ({ ...p, name: e.target.value }))} placeholder="Name" style={input} required />
            <input value={contractorForm.email} onChange={(e) => setContractorForm((p) => ({ ...p, email: e.target.value }))} placeholder="Email" style={input} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <select value={contractorForm.payoutMethod} onChange={(e) => setContractorForm((p) => ({ ...p, payoutMethod: e.target.value }))} style={input}>
                {['worldremit', 'lemfi', 'bank', 'paypal', 'other'].map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              <select value={contractorForm.defaultCurrency} onChange={(e) => setContractorForm((p) => ({ ...p, defaultCurrency: e.target.value }))} style={input}>
                {currencyOptions.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            <button type="submit" style={btn}>Add Contractor</button>
          </form>
          <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.35rem' }}>
            {contractorRows.map((c) => (
              <div key={c._id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.85rem', color: '#cbd5e1' }}>
                <span>{c.name} ({c.defaultCurrency || 'USD'})</span>
                <span><button onClick={() => removeContractor(c._id).then(refresh)} style={{ color: '#f87171' }}>archive</button></span>
              </div>
            ))}
          </div>
        </section>

        <section style={{ ...card, padding: '1rem' }}>
          <h2 style={{ fontSize: '1rem', marginBottom: '0.6rem' }}>Log Tax Remittance</h2>
          <form onSubmit={onAddTax} style={{ display: 'grid', gap: '0.5rem' }}>
            <input value={taxForm.jurisdiction} onChange={(e) => setTaxForm((p) => ({ ...p, jurisdiction: e.target.value }))} placeholder="Jurisdiction" style={input} />
            <input value={taxForm.periodLabel} onChange={(e) => setTaxForm((p) => ({ ...p, periodLabel: e.target.value }))} placeholder="Period" style={input} />
            <input type="number" min="0.01" step="0.01" value={taxForm.amount} onChange={(e) => setTaxForm((p) => ({ ...p, amount: e.target.value }))} placeholder="Amount" style={input} required />
            <input type="date" value={taxForm.paidAt} onChange={(e) => setTaxForm((p) => ({ ...p, paidAt: e.target.value }))} style={input} />
            <input value={taxForm.reference} onChange={(e) => setTaxForm((p) => ({ ...p, reference: e.target.value }))} placeholder="Reference ID" style={input} />
            <button type="submit" style={btn}>Add Tax Remit</button>
          </form>
          <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.35rem' }}>
            {taxRows.map((t) => (
              <div key={t._id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.85rem', color: '#cbd5e1' }}>
                <span>{t.jurisdiction} {t.periodLabel ? `- ${t.periodLabel}` : ''}</span>
                <span>{dollars(t.amountCents)} <button onClick={() => removeTaxRemittance(t._id).then(refresh)} style={{ color: '#f87171', marginLeft: '0.4rem' }}>x</button></span>
              </div>
            ))}
          </div>
        </section>

        <section style={{ ...card, padding: '1rem' }}>
          <h2 style={{ fontSize: '1rem', marginBottom: '0.6rem' }}>Time Entries</h2>
          <form onSubmit={onAddTimeEntry} style={{ display: 'grid', gap: '0.5rem' }}>
            <select value={timeEntryForm.project} onChange={(e) => setTimeEntryForm((p) => ({ ...p, project: e.target.value }))} style={input} required>
              <option value="">Select project</option>
              {(projects || []).map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            <select value={timeEntryForm.contractor} onChange={(e) => setTimeEntryForm((p) => ({ ...p, contractor: e.target.value }))} style={input} required>
              <option value="">Select contractor</option>
              {contractorRows.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <input type="number" min="0.1" step="0.1" value={timeEntryForm.hours} onChange={(e) => setTimeEntryForm((p) => ({ ...p, hours: e.target.value }))} placeholder="Hours" style={input} required />
              <input type="number" min="0.01" step="0.01" value={timeEntryForm.hourlyRate} onChange={(e) => setTimeEntryForm((p) => ({ ...p, hourlyRate: e.target.value }))} placeholder="Hourly rate" style={input} required />
            </div>
            <button type="submit" style={btn}>Add Time Entry</button>
          </form>
          <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.35rem' }}>
            {timeRows.map((t) => (
              <div key={t._id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.85rem', color: '#cbd5e1' }}>
                <span>{t.project?.name || 'Project'} - {t.contractorName} - {Number(t.hours || 0).toFixed(2)}h</span>
                <span>{dollars(t.amountCents)} {t.status !== 'billed' ? <button onClick={() => removeTimeEntry(t._id).then(refresh)} style={{ color: '#f87171', marginLeft: '0.4rem' }}>x</button> : null}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div style={{ ...card, marginTop: '1rem', padding: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: '1rem', margin: 0 }}>Log Contractor Payout</h2>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button type="button" onClick={onApproveAllVisibleDrafts} style={btnMuted}>
              Approve visible drafts
            </button>
            {['all', 'draft', 'approved', 'paid'].map((s) => (
              <button key={s} type="button" onClick={() => setPayoutStatusFilter(s)} style={{ ...btnMuted, opacity: payoutStatusFilter === s ? 1 : 0.7 }}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div style={{ ...hint, marginBottom: '0.7rem', display: 'grid', gap: '0.2rem' }}>
          <div><strong style={{ color: '#cbd5e1' }}>How this works:</strong> `source amount x FX rate - transfer fee = amount (USD)`.</div>
          <div>Use <strong style={{ color: '#cbd5e1' }}>draft</strong> while preparing, <strong style={{ color: '#cbd5e1' }}>approved</strong> when validated, and <strong style={{ color: '#cbd5e1' }}>paid</strong> only after money is sent.</div>
          <div>Project link is optional, but recommended for project profitability tracking.</div>
        </div>
        <form onSubmit={onAddPayout} style={{ display: 'grid', gap: '0.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.5rem' }}>
            <select value={payoutForm.contractor} onChange={(e) => onSelectContractor(e.target.value)} style={input}>
              <option value="">Select contractor (optional)</option>
              {contractorRows.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <input value={payoutForm.contractorName} onChange={(e) => setPayoutForm((p) => ({ ...p, contractorName: e.target.value }))} placeholder="Contractor name" style={input} required />
            <input value={payoutForm.contractorEmail} onChange={(e) => setPayoutForm((p) => ({ ...p, contractorEmail: e.target.value }))} placeholder="Contractor email" style={input} />
            <select value={payoutForm.payoutMethod} onChange={(e) => setPayoutForm((p) => ({ ...p, payoutMethod: e.target.value }))} style={input}>
              {['worldremit', 'lemfi', 'bank', 'paypal', 'other'].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <input type="number" min="0.01" step="0.01" value={payoutForm.amount} onChange={(e) => setPayoutForm((p) => ({ ...p, amount: e.target.value }))} placeholder="Amount (USD)" style={input} required />
            <select value={payoutForm.project} onChange={(e) => setPayoutForm((p) => ({ ...p, project: e.target.value }))} style={input}>
              <option value="">No project link</option>
              {(projects || []).map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            <input type="date" value={payoutForm.paidAt} onChange={(e) => setPayoutForm((p) => ({ ...p, paidAt: e.target.value }))} style={input} />
            <div style={{ display: 'grid', gap: '0.2rem' }}>
              <select value={payoutForm.status} onChange={(e) => setPayoutForm((p) => ({ ...p, status: e.target.value }))} style={input}>
                {['draft', 'approved', 'paid'].map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              <span style={hint}>Status flow: draft -> approved -> paid.</span>
            </div>
            <select value={payoutForm.sourceCurrency} onChange={(e) => setPayoutForm((p) => ({ ...p, sourceCurrency: e.target.value }))} style={input}>
              {currencyOptions.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <div style={{ display: 'grid', gap: '0.2rem' }}>
              <input type="number" min="0.01" step="0.01" value={payoutForm.sourceAmount} onChange={(e) => setPayoutForm((p) => ({ ...p, sourceAmount: e.target.value }))} placeholder="Source amount (optional)" style={input} />
              <span style={hint}>Amount in payout currency before conversion (e.g., GHS 50).</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.4rem' }}>
              <input type="number" min="0.0001" step="0.0001" value={payoutForm.fxRateToUsd} onChange={(e) => setPayoutForm((p) => ({ ...p, fxRateToUsd: e.target.value }))} placeholder="FX rate to USD" style={input} title="How many USD equals 1 unit of source currency (e.g., 1 GHS = 0.0909 USD)." />
              <button type="button" onClick={onFetchLiveFx} disabled={fxLoading} style={{ ...btnMuted, opacity: fxLoading ? 0.7 : 1 }}>
                {fxLoading ? 'Loading...' : 'Live FX'}
              </button>
            </div>
            <div style={{ display: 'grid', gap: '0.2rem' }}>
              <input type="number" min="0" step="0.01" value={payoutForm.transferFee} onChange={(e) => setPayoutForm((p) => ({ ...p, transferFee: e.target.value }))} placeholder="Transfer fee (USD)" style={input} />
              <span style={hint}>Provider fee deducted from converted payout amount.</span>
            </div>
          </div>
          <input value={payoutForm.note} onChange={(e) => setPayoutForm((p) => ({ ...p, note: e.target.value }))} placeholder="Note" style={input} />
          <button type="submit" style={btn}>Add Payout</button>
        </form>
        <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.45rem' }}>
          {payoutRows.map((p) => (
            <div key={p._id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.85rem', color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.35rem' }}>
              <span>{p.contractorName} - {(p.status || 'paid')} - {dollars(p.amountCents)}</span>
              <span style={{ display: 'flex', gap: '0.4rem' }}>
                {(p.status || 'paid') !== 'approved' ? <button onClick={() => changeTeamPayoutStatus(p._id, 'approved').then(refresh)} style={{ color: '#fbbf24' }}>approve</button> : null}
                {(p.status || 'paid') !== 'paid' ? <button onClick={() => changeTeamPayoutStatus(p._id, 'paid').then(refresh)} style={{ color: '#34d399' }}>mark paid</button> : null}
                <button onClick={() => removeTeamPayout(p._id).then(refresh)} style={{ color: '#f87171' }}>x</button>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...card, marginTop: '1rem', padding: '1rem' }}>
        <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem', color: '#cbd5e1' }}>Open Reminders</h3>
        {reminderRows.length === 0 ? <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>No open reminders.</div> : null}
        <div style={{ display: 'grid', gap: '0.35rem' }}>
          {reminderRows.map((r) => (
            <div key={r._id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', color: '#cbd5e1', fontSize: '0.85rem' }}>
              <span>{r.message}</span>
              <button onClick={() => resolveReminder(r._id).then(refresh)} style={{ color: '#34d399' }}>resolve</button>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...card, marginTop: '1rem', padding: '1rem' }}>
        <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem', color: '#cbd5e1' }}>Breakdowns (Current Range)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))', gap: '0.7rem' }}>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '0.35rem' }}>Expenses by category</div>
            {(Object.entries(breakdowns.expenseByCategory || {})).map(([x, v]) => (
              <div key={x} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#e2e8f0' }}><span>{x}</span><span>{dollars(v)}</span></div>
            ))}
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '0.35rem' }}>Payouts by method</div>
            {(Object.entries(breakdowns.payoutByMethod || {})).map(([x, v]) => (
              <div key={x} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#e2e8f0' }}><span>{x}</span><span>{dollars(v)}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
